/**
 * Trackers and longitudinal studies say how each wave is fielded (UAT UI-018).
 * PURE.
 *
 * A tracker of exit interviews is an exit interview, wave after wave: its pool
 * is the store visitors, its rates the exit rates. So a wave runs as the
 * method it is fielded as (`fieldedAs`): that study type's pool, stages and
 * rates. A longitudinal study keeps its own follow-through (the same people
 * answer every wave), read at the longitudinal rate, on top of the method's
 * funnel. Without `fieldedAs` a wave is a standard survey, as before.
 *
 * The stored request keeps `studyType` (tracker or longitudinal), which drives
 * lockout (lockout.ts) and the labels; only the engine reads the wave.
 */
import type { StudyTypeDef } from './registry';
import type { FeasibilityRequest } from './request';

/** Study types that run in waves. */
export const WAVE_STUDY_TYPES: readonly string[] = ['tracker', 'longitudinal'];

export const isWaveStudy = (studyType: string) => WAVE_STUDY_TYPES.includes(studyType);

/** Why `fieldedAs` cannot be used, or null when it can (or is not set). */
export function fieldedAsIssue(
    request: Pick<FeasibilityRequest, 'studyType' | 'fieldedAs'>,
    studyTypes: Pick<StudyTypeDef, 'studyTypeKey' | 'label' | 'requiresEscalation'>[],
): string | null {
    if (!request.fieldedAs) return null;
    const base = studyTypes.find((s) => s.studyTypeKey === request.studyType);
    if (!isWaveStudy(request.studyType))
        return `"Fielded as" is for trackers and longitudinal studies, not ${base?.label ?? request.studyType}.`;
    const method = studyTypes.find((s) => s.studyTypeKey === request.fieldedAs);
    if (!method) return `Unknown method "${request.fieldedAs}" for each wave.`;
    if (isWaveStudy(method.studyTypeKey))
        return 'Pick how each wave is fielded, such as a standard survey or an exit interview.';
    if (method.requiresEscalation)
        return `${method.label} is reviewed by hand, so it cannot be how a wave is fielded here.`;
    return null;
}

/** The request one wave runs as: the method's study type in place of the tracker's. */
export function asWave(request: FeasibilityRequest): FeasibilityRequest {
    if (!request.fieldedAs || !isWaveStudy(request.studyType)) return request;
    return { ...request, studyType: request.fieldedAs };
}

/**
 * The study type one wave runs as. A longitudinal wave adds its own
 * follow-through stage and phases to the method, read at the longitudinal
 * rate (`followThroughKey`).
 */
export function waveStudyType(
    studyTypes: StudyTypeDef[],
    request: Pick<FeasibilityRequest, 'studyType' | 'fieldedAs'>,
): StudyTypeDef | undefined {
    const base = studyTypes.find((s) => s.studyTypeKey === request.studyType);
    if (!base || !request.fieldedAs || !isWaveStudy(base.studyTypeKey)) return base;
    const method = studyTypes.find((s) => s.studyTypeKey === request.fieldedAs);
    if (!method) return undefined;
    if (!base.stages.includes('follow_through') || method.stages.includes('follow_through'))
        return { ...method, label: `${base.label} (each wave ${lower(method.label)})` };
    // Follow-through sits where the longitudinal funnel has it: before panel growth.
    const at = method.stages.indexOf('panel_growth');
    const stages = [...method.stages];
    stages.splice(at < 0 ? stages.length : at, 0, 'follow_through');
    return {
        ...method,
        label: `${base.label} (each wave ${lower(method.label)})`,
        stages,
        phases: Math.max(base.phases, method.phases),
        followThroughKey: base.studyTypeKey,
    };
}

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
